import React, { useEffect, useState } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import VouchersAPI, { normalizeVoucher } from "../../../api/v3/vouchers-api-v3";

const BASE = "/app/sales/bagspa";

// datetime-local expects "YYYY-MM-DDTHH:mm"
const toLocalDatetimeValue = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

export default function VoucherFormPage() {
  const { id } = useParams();
  const history = useHistory();

  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      try {
        // Backend returns { item: {...} } (or flat)
        const data = await VouchersAPI.show(id);
        const raw = data?.item ?? data ?? {};
        const base = normalizeVoucher(raw);

        // Pre-fill text inputs for slugs (accept array or string from backend)
        const toCSV = (v) =>
          Array.isArray(v) ? v.join(", ") : (v || "");

        setForm({
          ...base,
          maximum_discount_value:
            raw.maximum_discount_value !== undefined ? raw.maximum_discount_value : null,
          minimum_transaction_value:
            raw.minimum_transaction_value !== undefined ? raw.minimum_transaction_value : null,
          // prefer explicit *_slugs from backend, else normalized arrays
          product_category_slugs: toCSV(raw.product_category_slugs ?? base.category_codes),
          order_category_slugs: toCSV(raw.order_category_slugs ?? base.order_category_codes),
        });
      } catch (e) {
        setErr(e.message || "Failed to load voucher");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const updateField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  async function onSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      // Build nested payload (backend expects params[:voucher])
      const voucher = {
        code: form.code, // immutable in UI
        discount_type: form.discount_type || "fixed_amount",
        discount_value: Number(form.discount_value || 0),
        maximum_discount_value:
          form.maximum_discount_value === "" || form.maximum_discount_value == null
            ? null
            : Number(form.maximum_discount_value),
        minimum_transaction_value:
          form.minimum_transaction_value === "" || form.minimum_transaction_value == null
            ? null
            : Number(form.minimum_transaction_value),
        expiration_date: form.expiration_date || null,
        max_redemption_per_user: Number(form.max_redemption_per_user || 1),
        active: !!form.active,
        is_promoted: !!form.is_promoted,
        promotional_text: form.promotional_text || null,
        applies_to_scope: form.applies_to_scope || "all_items",
      };

      // Top-level slugs (controller maps these into voucher fields)
      const body = {
        voucher,
        product_category_slugs: (form.product_category_slugs || "").trim(),
        order_category_slugs: (form.order_category_slugs || "").trim(),
      };

      await VouchersAPI.update(id, body);
      history.push(`${BASE}/vouchers/${id}`);
    } catch (e) {
      setErr(e.message || "Failed to save voucher");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="container-fluid">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0">Edit Voucher</h2>
        <div className="d-flex gap-2">
          <Link className="btn btn-outline-secondary" to={`${BASE}/vouchers/${id}`}>
            Cancel
          </Link>
          <button className="btn btn-primary" form="voucherForm" type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {loading && <div>Loading…</div>}
      {err && <div className="alert alert-danger">{err}</div>}

      {!loading && (
        <form id="voucherForm" onSubmit={onSubmit} className="card shadow-sm">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Code</label>
                <input
                  className="form-control"
                  value={form.code || ""}
                  onChange={(e) => updateField("code", e.target.value)}
                  readOnly
                />
                <div className="form-text">Code is immutable for safety.</div>
              </div>

              <div className="col-md-3">
                <label className="form-label">Discount Type</label>
                <select
                  className="form-select"
                  value={form.discount_type || "fixed_amount"}
                  onChange={(e) => updateField("discount_type", e.target.value)}
                >
                  <option value="fixed_amount">fixed_amount</option>
                  <option value="percentage">percentage</option>
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">Discount Value</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  value={form.discount_value ?? ""}
                  onChange={(e) => updateField("discount_value", e.target.value)}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">Max Discount (optional)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  value={form.maximum_discount_value ?? ""}
                  onChange={(e) => updateField("maximum_discount_value", e.target.value)}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">Min Transaction (optional)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  value={form.minimum_transaction_value ?? ""}
                  onChange={(e) => updateField("minimum_transaction_value", e.target.value)}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">Max Redemption/User</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.max_redemption_per_user ?? 1}
                  onChange={(e) => updateField("max_redemption_per_user", e.target.value)}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">Expiration (optional)</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={toLocalDatetimeValue(form.expiration_date)}
                  onChange={(e) => updateField("expiration_date", e.target.value)}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">Active?</label>
                <div className="form-check">
                  <input
                    id="active"
                    type="checkbox"
                    className="form-check-input"
                    checked={!!form.active}
                    onChange={(e) => updateField("active", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="active">
                    Active
                  </label>
                </div>
              </div>

              <div className="col-md-12">
                <label className="form-label">Promotional Text</label>
                <input
                  className="form-control"
                  value={form.promotional_text || ""}
                  onChange={(e) => updateField("promotional_text", e.target.value)}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Applies To</label>
                <select
                  className="form-select"
                  value={form.applies_to_scope || "all_items"}
                  onChange={(e) => updateField("applies_to_scope", e.target.value)}
                >
                  <option value="all_items">all_items</option>
                  <option value="product_categories">product_categories</option>
                  <option value="order_categories">order_categories</option>
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label">Product Category Slugs (comma)</label>
                <input
                  className="form-control"
                  placeholder="BG, WL"
                  value={form.product_category_slugs || ""}
                  onChange={(e) => updateField("product_category_slugs", e.target.value)}
                />
                <div className="form-text">
                  Used when applies_to_scope = <b>product_categories</b>
                </div>
              </div>

              <div className="col-md-4">
                <label className="form-label">Order Category/Treatment Codes (comma)</label>
                <input
                  className="form-control"
                  placeholder="FULL_TREATMENT-BAG-LEATHER-S, CLEANING-SHOES-M"
                  value={form.order_category_slugs || ""}
                  onChange={(e) => updateField("order_category_slugs", e.target.value)}
                />
                <div className="form-text">
                  Used when applies_to_scope = <b>order_categories</b>
                </div>
              </div>

              <div className="col-md-3">
                <label className="form-label">Promoted?</label>
                <div className="form-check">
                  <input
                    id="is_promoted"
                    type="checkbox"
                    className="form-check-input"
                    checked={!!form.is_promoted}
                    onChange={(e) => updateField("is_promoted", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="is_promoted">
                    is_promoted
                  </label>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
